<script setup>
import { inject, ref, onBeforeMount, computed, onMounted } from 'vue'
import { UAParser } from 'ua-parser-js'
import InlineSvg from 'vue-inline-svg'
import { userReportAbuse } from '@/api/api.js'
import app from '@/main.js'
import ErrorMessage from '@/components/Functional/ErrorMessage.vue'
import reportModalState, { resetState } from '@/components/ModalsNext/ReportModal/Store/report-modal-state.js'
import ReportModalReasons from '@/components/ModalsNext/ReportModal/Includes/ReportModalReasons.vue'
import { useTimeout } from '@vueuse/core'
import { useResizeObserver } from '@vueuse/core'

const props = defineProps({
  userId: {
    type: String,
    required: true
  },
  source: {
    type: String,
    required: true
  },
  messageId: {
    type: String,
    required: false,
    default: null
  }
})

onBeforeMount(() => {
  resetState()
})

const {
  selectedReason,
  errorMessage
} = reportModalState

const close = inject('close')
const { device } = UAParser()

const loading = ref(false)
const reported = ref(false)
const { ready, start, stop } = useTimeout(600, { controls: true, immediate: false })

const isLoading = computed(() => {
  return loading.value || !ready.value
})

async function report () {
  try {
    if (!selectedReason.value) {
      errorMessage.value = 'Please select a reason to report.'
      return
    }

    loading.value = true
    start()

    const { error } = await userReportAbuse({
      userId: props.userId,
      reasonId: selectedReason.value,
      source: props.source,
      messageId: props.messageId
    })

    if (error) {
      throw new Error(error)
    }

    reported.value = true
    loading.value = false
    await new Promise((resolve) => setTimeout(resolve, 1500))
    close()
  } catch (error) {
    console.error('Error reporting user:', error)
    stop()
    loading.value = false
    errorMessage.value = 'An error occurred while reporting the user. Please try again later.'
  }
}

const el = ref(null)
const hasVerticalScroll = ref(false)

const checkScroll = () => {
  if (!el.value) {
    return
  }

  hasVerticalScroll.value = el.value.scrollHeight > el.value.clientHeight
}

onMounted(() => {
  checkScroll()
})

useResizeObserver(el, checkScroll)
</script>

<template>
  <div
    ref="el"
    :class="[device.is('mobile') ? 'rounded-t-xl self-end' : 'rounded-xl self-center']"
    class="bg-white p-4 shadow-lg w-full relative max-h-[96%] overflow-y-auto thin-scrollbar"
    @click.stop>
    <div class="flex items-center justify-between mb-6">
      <div/>

      <h2 class="text-lg font-bold text-gray-900">
        {{ messageId && source === 'chat_message' ? app.$i18n.t('report.report_this_message') : app.$i18n.t('report.report_this_creator') }}
      </h2>

      <button type="button" @click="close">
        <InlineSvg
          class="h-4 w-4"
          :src="require('@/assets/fontawesome/regular/xmark.svg')"/>
      </button>
    </div>

    <ReportModalReasons/>

    <ErrorMessage
      v-if="errorMessage"
      class="mb-3"
      :message="errorMessage"/>

    <div
      :class="{
        'sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur': hasVerticalScroll
      }"
      class="grid grid-cols-2 gap-2">
      <button
        type="submit"
        class="btn btn-block btn-secondary"
        @click="close">
        {{ app.$i18n.t('common.cancel') }}
      </button>

      <button
        type="submit"
        :disabled="isLoading || reported"
        :class="{
          '!bg-green-500 !hover:bg-green-500': reported && !isLoading,
          '!hover:bg-primary': isLoading
        }"
        class="btn btn-block btn-primary h-[2.875rem] relative disabled:!opacity-100"
        @click="report">
        <span :class="{ 'invisible': reported || isLoading }">
          {{ app.$i18n.t('common.yes') }}
        </span>

        <InlineSvg
          v-if="reported && !isLoading"
          class="h-6 w-6 absolute left-0 right-0 top-0 bottom-0 m-auto"
          :src="require('@/assets/fontawesome/regular/check.svg')"/>

        <span
          v-show="isLoading"
          class="absolute inset-0 m-auto loader !border-2 w-6 h-6"/>
      </button>

      <div
        v-if="hasVerticalScroll"
        class="pointer-events-none absolute left-0 right-0 bottom-[calc(2.875rem)] h-6 -mx-4 bg-gradient-to-t from-white to-transparent" />
    </div>
  </div>
</template>
